import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, LayoutDashboard, Megaphone, Users, DollarSign } from 'lucide-react';
const features = [{
  icon: <LayoutDashboard className="w-5 h-5" data-id="element-6182" />,
  title: 'See the whole roster clearly',
  description: 'Track every active campaign, route gap, offer, hold, contract, and deposit across your artists.',
  mockup: <div className="bg-[#0F0F14] border border-border rounded-lg p-4 h-64 overflow-hidden" data-id="element-6183">
        <div className="text-xs font-semibold text-textMuted uppercase tracking-wider mb-3" data-id="element-6184">
          Roster Overview
        </div>
        <div className="space-y-2" data-id="element-6185">
          {[{
        artist: 'The Wildwoods',
        campaigns: 2,
        offers: 5,
        confirmed: 8,
        status: 'active'
      }, {
        artist: 'Sarah Jarosz',
        campaigns: 1,
        offers: 3,
        confirmed: 12,
        status: 'active'
      }, {
        artist: 'Mark Erelli',
        campaigns: 1,
        offers: 2,
        confirmed: 6,
        status: 'planning'
      }].map(item => <div key={item.artist} className="bg-surface border border-border rounded-lg p-3" data-id="element-6186">
              <div className="flex justify-between items-center mb-2" data-id="element-6187">
                <div className="text-sm font-semibold" data-id="element-6188">{item.artist}</div>
                <div className={`px-2 py-1 text-xs rounded ${item.status === 'active' ? 'bg-green-500/10 text-green-500' : 'bg-amber-500/10 text-amber-500'}`} data-id="element-6189">
                  {item.status}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs" data-id="element-6190">
                <div data-id="element-6191">
                  <div className="text-textMuted" data-id="element-6192">Campaigns</div>
                  <div className="font-medium" data-id="element-6193">{item.campaigns}</div>
                </div>
                <div data-id="element-6194">
                  <div className="text-textMuted" data-id="element-6195">Offers</div>
                  <div className="font-medium text-primary" data-id="element-6196">{item.offers}</div>
                </div>
                <div data-id="element-6197">
                  <div className="text-textMuted" data-id="element-6198">Confirmed</div>
                  <div className="font-medium text-green-500" data-id="element-6199">
                    {item.confirmed}
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </div>
}, {
  icon: <Megaphone className="w-5 h-5" data-id="element-6200" />,
  title: 'Move faster on every deal',
  description: 'Templates, AI support, shared inboxes, and structured approvals help your team close more bookings with less chaos.',
  mockup: <div className="bg-[#0F0F14] border border-border rounded-lg p-4 h-64" data-id="element-6201">
        <div className="text-xs font-semibold text-textMuted uppercase tracking-wider mb-3" data-id="element-6202">
          Campaign Pipeline
        </div>
        <div className="space-y-3" data-id="element-6203">
          <div className="bg-surface border border-border rounded-lg p-3" data-id="element-6204">
            <div className="flex justify-between items-center mb-2" data-id="element-6205">
              <div className="text-sm font-semibold" data-id="element-6206">Summer Run '26</div>
              <div className="text-xs text-textMuted" data-id="element-6207">The Wildwoods</div>
            </div>
            <div className="flex items-center gap-2 mb-2" data-id="element-6208">
              <div className="flex-1 h-2 bg-background rounded-full overflow-hidden" data-id="element-6209">
                <div className="h-full bg-primary" style={{
              width: '65%'
            }} data-id="element-6210"></div>
              </div>
              <div className="text-xs text-textMuted" data-id="element-6211">65%</div>
            </div>
            <div className="grid grid-cols-4 gap-2 text-xs" data-id="element-6212">
              <div data-id="element-6213">
                <div className="text-textMuted" data-id="element-6214">Targets</div>
                <div className="font-medium" data-id="element-6215">42</div>
              </div>
              <div data-id="element-6216">
                <div className="text-textMuted" data-id="element-6217">Contacted</div>
                <div className="font-medium" data-id="element-6218">28</div>
              </div>
              <div data-id="element-6219">
                <div className="text-textMuted" data-id="element-6220">Offers</div>
                <div className="font-medium text-primary" data-id="element-6221">6</div>
              </div>
              <div data-id="element-6222">
                <div className="text-textMuted" data-id="element-6223">Confirmed</div>
                <div className="font-medium text-green-500" data-id="element-6224">3</div>
              </div>
            </div>
          </div>
          <div className="bg-surface border border-border rounded-lg p-3 opacity-60" data-id="element-6225">
            <div className="flex justify-between items-center mb-2" data-id="element-6226">
              <div className="text-sm font-semibold" data-id="element-6227">Fall Tour '26</div>
              <div className="text-xs text-textMuted" data-id="element-6228">Sarah Jarosz</div>
            </div>
            <div className="flex items-center gap-2" data-id="element-6229">
              <div className="flex-1 h-2 bg-background rounded-full overflow-hidden" data-id="element-6230">
                <div className="h-full bg-primary" style={{
              width: '25%'
            }} data-id="element-6231"></div>
              </div>
              <div className="text-xs text-textMuted" data-id="element-6232">25%</div>
            </div>
          </div>
        </div>
      </div>
}, {
  icon: <Users className="w-5 h-5" data-id="element-6233" />,
  title: 'Keep your team in sync',
  description: 'Assign deals, share inboxes, set approval chains, and track workload across your booking team.',
  mockup: <div className="bg-[#0F0F14] border border-border rounded-lg p-4 h-64" data-id="element-6234">
        <div className="text-xs font-semibold text-textMuted uppercase tracking-wider mb-3" data-id="element-6235">
          Team Workload
        </div>
        <div className="space-y-2" data-id="element-6236">
          {[{
        name: 'Alex',
        role: 'Senior Booker',
        assigned: 12,
        pending: 3
      }, {
        name: 'Jordan',
        role: 'Booker',
        assigned: 8,
        pending: 2
      }, {
        name: 'Sam',
        role: 'Coordinator',
        assigned: 15,
        pending: 5
      }].map(member => <div key={member.name} className="bg-surface border border-border rounded-lg p-3" data-id="element-6237">
              <div className="flex justify-between items-center mb-2" data-id="element-6238">
                <div data-id="element-6239">
                  <div className="text-sm font-semibold" data-id="element-6240">{member.name}</div>
                  <div className="text-xs text-textMuted" data-id="element-6241">{member.role}</div>
                </div>
                <div className="text-right" data-id="element-6242">
                  <div className="text-xs text-textMuted" data-id="element-6243">Assigned</div>
                  <div className="text-sm font-medium" data-id="element-6244">{member.assigned}</div>
                </div>
              </div>
              {member.pending > 0 && <div className="px-2 py-1 bg-amber-500/10 text-amber-500 text-xs rounded" data-id="element-6245">
                  {member.pending} pending approval
                </div>}
            </div>)}
        </div>
      </div>
}, {
  icon: <DollarSign className="w-5 h-5" data-id="element-6246" />,
  title: 'Keep finance and execution tight',
  description: 'Generate invoices, track deposits, monitor settlement, and keep the road team aligned once dates are confirmed.',
  mockup: <div className="bg-[#0F0F14] border border-border rounded-lg p-4 h-64" data-id="element-6247">
        <div className="text-xs font-semibold text-textMuted uppercase tracking-wider mb-3" data-id="element-6248">
          Finance Dashboard
        </div>
        <div className="space-y-3" data-id="element-6249">
          <div className="grid grid-cols-3 gap-2" data-id="element-6250">
            <div className="bg-surface border border-border rounded-lg p-3" data-id="element-6251">
              <div className="text-xs text-textMuted mb-1" data-id="element-6252">Total Expected</div>
              <div className="text-lg font-bold text-primary" data-id="element-6253">$142K</div>
            </div>
            <div className="bg-surface border border-border rounded-lg p-3" data-id="element-6254">
              <div className="text-xs text-textMuted mb-1" data-id="element-6255">Collected</div>
              <div className="text-lg font-bold text-green-500" data-id="element-6256">$89K</div>
            </div>
            <div className="bg-surface border border-border rounded-lg p-3" data-id="element-6257">
              <div className="text-xs text-textMuted mb-1" data-id="element-6258">Outstanding</div>
              <div className="text-lg font-bold text-amber-500" data-id="element-6259">$53K</div>
            </div>
          </div>
          <div className="bg-surface border border-border rounded-lg p-3" data-id="element-6260">
            <div className="text-xs text-textMuted mb-2" data-id="element-6261">Aging Receivables</div>
            <div className="space-y-1.5" data-id="element-6262">
              <div className="flex justify-between text-xs" data-id="element-6263">
                <span className="text-textMuted" data-id="element-6264">0-30 days</span>
                <span className="font-medium" data-id="element-6265">$28K</span>
              </div>
              <div className="flex justify-between text-xs" data-id="element-6266">
                <span className="text-textMuted" data-id="element-6267">31-60 days</span>
                <span className="font-medium text-amber-500" data-id="element-6268">$18K</span>
              </div>
              <div className="flex justify-between text-xs" data-id="element-6269">
                <span className="text-textMuted" data-id="element-6270">60+ days</span>
                <span className="font-medium text-red-500" data-id="element-6271">$7K</span>
              </div>
            </div>
          </div>
        </div>
      </div>
}];
export function ForAgentsPage() {
  return <div data-id="element-6272">
      {/* Hero */}
      <section className="relative pt-40 pb-20 overflow-hidden" data-id="element-6273">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-primary/5 rounded-full blur-[100px] pointer-events-none" data-id="element-6274"></div>

        <div className="max-w-4xl mx-auto px-6 text-center relative z-10" data-id="element-6275">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-5xl md:text-6xl font-bold tracking-tight mb-6 leading-tight" data-id="element-6276">
            Run your roster like an operating system, not a spreadsheet.
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="text-lg md:text-xl text-textMuted mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-6277">
            Manage multiple artists, campaigns, deals, contracts, payments, and
            team workflows from one place built for speed and visibility.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-id="element-6278">
            <button className="inline-flex items-center gap-2 px-8 py-4 text-base font-medium text-textMain border border-border rounded-lg hover:bg-surface transition-colors" data-id="element-6279">
              Book a demo
              <ChevronRight className="w-4 h-4" data-id="element-6280" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      {features.map((feature, index) => <section key={index} className={`py-20 ${index % 2 === 0 ? 'bg-surface/10 border-y border-border' : ''}`} data-id="element-6281">
          <div className="max-w-6xl mx-auto px-6" data-id="element-6282">
            <div className={`grid md:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`} data-id="element-6283">
              <motion.div initial={{
            opacity: 0,
            x: index % 2 === 0 ? -30 : 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className={index % 2 === 1 ? 'md:order-2' : ''} data-id="element-6284">
                <div className="w-12 h-12 rounded-lg bg-background border border-border flex items-center justify-center text-primary mb-6" data-id="element-6285">
                  {feature.icon}
                </div>
                <h2 className="text-2xl md:text-3xl font-bold mb-4" data-id="element-6286">
                  {feature.title}
                </h2>
                <p className="text-lg text-textMuted leading-relaxed" data-id="element-6287">
                  {feature.description}
                </p>
              </motion.div>

              <motion.div initial={{
            opacity: 0,
            x: index % 2 === 0 ? 30 : -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className={index % 2 === 1 ? 'md:order-1' : ''} data-id="element-6288">
                {feature.mockup}
              </motion.div>
            </div>
          </div>
        </section>)}

      {/* Final CTA */}
      <section className="py-24 border-t border-border" data-id="element-6289">
        <div className="max-w-3xl mx-auto px-6 text-center" data-id="element-6290">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-6291">
            Ready to scale your roster?
          </h2>
          <p className="text-lg text-textMuted mb-10" data-id="element-6292">
            Join agencies who are closing more deals with less operational
            chaos.
          </p>
          <button className="inline-flex items-center gap-2 px-8 py-4 text-base font-medium text-textMain border border-border rounded-lg hover:bg-surface transition-colors" data-id="element-6293">
            Book a demo
            <ChevronRight className="w-4 h-4" data-id="element-6294" />
          </button>
        </div>
      </section>
    </div>;
}